import { Link } from "react-router-dom";
import { ArrowRight, HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqItems = [
  {
    value: "what-is-pdfworkspace",
    question: "What is PDFWorkSpace?",
    answer:
      "PDFWorkSpace is a free online PDF workspace where you can edit, organize, OCR, sign, and study PDFs in your browser. If you are looking for a private PDF space that keeps files on your device, that is exactly what this tool is built for.",
  },
  {
    value: "upload",
    question: "Do my PDFs get uploaded?",
    answer:
      "No. PDFWorkSpace runs directly in your browser. The tools work in memory only and do not send files to a server.",
  },
  {
    value: "storage",
    question: "Is anything stored after I close the tab?",
    answer:
      "No. Once you close the tab, there is no copy of your files here. Recent history only keeps tool names and filenames for this session.",
  },
  {
    value: "history",
    question: "What exactly does recent history track?",
    answer:
      "It records which tool you used, the filenames involved, whether it succeeded or failed, and a short technical note for errors. It resets when you refresh or close the tab.",
  },
  {
    value: "mobile",
    question: "Can I use PDFWorkSpace on my phone or tablet?",
    answer:
      "Yes. PDFWorkSpace works on any modern browser, including mobile devices. However, processing large PDFs may be slower on phones due to hardware limitations.",
  },
  {
    value: "offline",
    question: "Does PDFWorkSpace work offline?",
    answer:
      "Once the page is loaded, most tools can work without an internet connection since all processing happens locally in your browser.",
  },
  {
    value: "audience",
    question: "Who is PDFWorkSpace for?",
    answer:
      "People who hesitate before dragging a PDF into a random website: lawyers, founders, doctors, and anyone working with documents that cannot leak.",
  },
];

export const LandingFaqSection = () => {
  return (
    <section className="bg-background py-16 md:py-20">
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid gap-12 lg:grid-cols-5">
          {/* Left Side - Header */}
          <div className="lg:col-span-2 lg:sticky lg:top-24 lg:self-start">
            <div className="space-y-6">
              <span className="inline-block rounded-full bg-secondary px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-text-secondary">
                FAQ
              </span>
              <h2 className="font-serifAccent text-3xl tracking-tight text-foreground md:text-4xl">
                Questions, briefly
              </h2>
              <p className="text-md text-text-secondary leading-relaxed">
                Common questions about how PDFWorkSpace works and protects
                your privacy.
              </p>

              <div className="flex items-center gap-4 pt-4">
                <Link
                  to="/faq"
                  className="group inline-flex items-center gap-2 rounded-full border border-foreground px-5 py-2.5 text-sm font-medium text-foreground transition-all hover:bg-foreground hover:text-background"
                >
                  View all FAQs
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>

              {/* Help badge */}
              <div className="mt-8 flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                <div className="rounded-lg bg-secondary p-2">
                  <HelpCircle className="h-5 w-5 text-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Still have questions?
                  </p>
                  <p className="text-xs text-text-secondary mt-1">
                    Check our{" "}
                    <Link
                      to="/about-privacy"
                      className="underline underline-offset-2 hover:text-foreground"
                    >
                      About & Privacy
                    </Link>{" "}
                    page for more details.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Accordion */}
          <div className="lg:col-span-3">
            <Accordion type="single" collapsible className="space-y-3">
              {faqItems.map((item, index) => (
                <AccordionItem
                  key={item.value}
                  value={item.value}
                  className="rounded-xl border border-border bg-card px-5 py-1 transition-all duration-200 hover:border-foreground/20 data-[state=open]:bg-secondary/30 data-[state=open]:border-foreground/20 opacity-0 animate-fade-in-up"
                  style={{ animationDelay: `${0.1 + index * 0.08}s` }}
                >
                  <AccordionTrigger className="text-md text-foreground hover:no-underline py-4">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-md text-text-secondary pb-4 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};
